import React, { forwardRef, ForwardedRef, Ref, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { AriaComboBoxOptions } from 'react-aria/useComboBox';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useVisuallyHidden } from 'react-aria/VisuallyHidden';
import { useFocusableRef } from '@react-spectrum/utils';
import { Node, MultipleSelection } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/select/select.module.css';

import { useThemeCheck } from '../../utils';
import { SelectBase } from './SelectBase';
import { MultiSelectProps } from './MultiSelect';
import {
  MultiComboBoxStateOptions,
  useMultiComboBoxState,
} from './useMultiComboBoxState';
import { useMultiComboBox } from './useMultiComboBox';
import { AriaComboBoxPropsKeys, BaseComponentKeys } from '../../utils/types';

export type SearchableMultiSelectProps<T extends object> = Omit<
  MultiSelectProps<T>,
  'resolveSelectAllToKeys'
> &
  Pick<MultipleSelection, 'onSelectionChange'> &
  Pick<
    AriaComboBoxOptions<T>,
    | 'defaultItems'
    | 'inputValue'
    | 'defaultInputValue'
    | 'onInputChange'
    | 'name'
    | AriaComboBoxPropsKeys
    | BaseComponentKeys
  > &
  Pick<
    MultiComboBoxStateOptions<T>,
    'groupSelectedItemsOnTop' | 'noInputResetOnSelect'
  > & {
    /**
     * Filter function used when `items` is not controlled, to determine which
     * items should be included in the dropdown, based on a search query. By
     * default, a "String.includes" filter is used on the "textValue" of the
     * node.
     */
    filter?: (node: Node<T>, searchQuery: string) => boolean;
  };

/**
 * Similar to `Select` but allows user to type and filter options.
 *
 * Example:
 *
 * ```tsx
 * <SearchableMultiSelect label="Label text" placeholder="Select an option">
 *  <SelectItem>Option 1</SelectItem>
 *  <SelectItem>Option 2</SelectItem>
 *  <SelectItem>Option 3</SelectItem>
 *  <SelectItem>Option 4</SelectItem>
 * </Select>
 * ```
 *
 * @see Select
 * @see SelectButton
 */
function _SearchableMultiSelect<T extends object>(
  props: SearchableMultiSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { error, note, size = 'lg', labelPlacement = 'above' } = props;
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLDivElement>(null);
  const listBoxRef = useFocusableRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { visuallyHiddenProps } = useVisuallyHidden({});
  const focusRingAria = useFocusRing(props);
  const {
    renderSelectionText = (selection) =>
      selection === 'all' ? 'All selected' : `${selection.size} selected`,
    filter = (node, search): boolean =>
      node.textValue.toLowerCase().includes(search.toLowerCase()),
  } = props;

  const state = useMultiComboBoxState({
    ...props,
    allowsEmptyCollection: props.emptyState !== null,
    filter,
    renderSelectionText,
  });

  useEffect(() => {
    if (state.isOpen) {
      // menu just opened. Focus the input
      inputRef.current?.focus();
    }
  }, [state.isOpen]);

  const { onBlur, onFocus, onFocusChange, ...filteredProps } = props;
  const {
    labelProps,
    buttonProps,
    inputProps,
    listBoxProps: menuProps,
    descriptionProps,
    errorMessageProps,
  } = useMultiComboBox(
    {
      ...filteredProps,
      errorMessage: error,
      description: note,
      popoverRef,
      buttonRef: triggerRef,
      inputRef,
      listBoxRef,
    },
    state
  );

  const selectionText =
    state.selectedItems.length === 0
      ? props.placeholder
      : state.selectedItems.length === 1
      ? state.selectedItems[0].textValue
      : renderSelectionText(
          state.selectionManager.isSelectAll
            ? 'all'
            : state.selectionManager.selectedKeys,
          state.selectedItems
        );

  /**
   * `useComboBox` makes the trigger only secondary for the UI and excludes it
   * from tabbing order. We don't want the same behavior. The trigger is the
   * main interactive element.
   */
  const triggerProps: typeof buttonProps = {
    ...buttonProps,
    onBlur,
    onFocus,
    onFocusChange,
  };

  const showInput = state.isFocused || state.isOpen;

  return (
    <SelectBase
      multiple
      state={state}
      containerRef={ref}
      listBoxRef={listBoxRef}
      popoverRef={popoverRef}
      triggerRef={triggerRef}
      selectProps={props}
      selectAria={{
        labelProps,
        triggerProps,
        valueProps: {},
        menuProps,
        descriptionProps,
        errorMessageProps,
      }}
      focusRingAria={focusRingAria}
      isNonModal
      size={size}
      labelPlacement={labelPlacement}
      selectValueContent={
        <>
          <input
            {...(showInput ? {} : visuallyHiddenProps)}
            {...inputProps}
            ref={inputRef}
            tabIndex={-1} // The input should not be tabbable. The trigger should
            onBlur={(e) => {
              // In some cases @react-aria/focus' virtual focus manager emits a
              // "focusout" event when virtually focusing the option in the
              // listbox.
              // https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/virtualFocus.ts#L15
              //
              // That event was dispatched by the code, so it has `isTrusted` set to `false`.
              //
              // This started happening since this commit https://github.com/adobe/react-spectrum/commit/cdba748760b1e30fb15414e8b585d05fd167819e
              //
              // Could not find a better way to preserve all behaviour described by our tests.
              if (e.isTrusted) {
                state.setFocused(false);
              }
            }}
            className={styles['sapphire-select__search-input']}
          />
          {!showInput &&
            (state.selectedItems.length === 0 ? (
              <div className={clsx(styles['sapphire-select__placeholder'])}>
                {selectionText}
              </div>
            ) : (
              <>{selectionText}</>
            ))}
        </>
      }
    />
  );
}

export const SearchableMultiSelect = forwardRef(_SearchableMultiSelect) as <
  T extends object
>(
  props: SearchableMultiSelectProps<T> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
